import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonNew } from "components/buttonNew";
import type { TBuilderToolbarMenuViewPresentation } from "components/builderShell/BuilderToolbarMenuView";
import { BuilderToolbarMenuView } from "components/builderShell/BuilderToolbarMenuView";
import type { IBuilderToolbarMenuViewSection } from "components/builderShell/types";
import "components/builderShell/builderToolbarMenuView.scss";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import {
  GetEmailMarketingTemplate,
  GetEmailMarketingTemplates,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import type { IEmailMarketingTemplateListItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplatesTypes";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError } from "redux/actions/ui";
import {
  emailBuilderCloseBlockSettings,
  emailBuilderCloseInlineContent,
  emailBuilderSyncEditorDocument,
} from "redux/actions/emailBuilder/emailBuilder";
import { runEditorThunk } from "../../../domain/store";
import { editorThunks } from "../../../domain/redux/thunks";
import { EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT } from "../../../emailBuilderConstants";
import {
  emailBuilderTemplatesHasMore,
  filterEmailBuilderTemplatesPanelList,
  normalizeEmailBuilderTemplateActiveDocument,
} from "../../../operations/applyEmailBuilderTemplate";
import { EmailBuilderTemplatePickerCard } from "../../../templatePicker/EmailBuilderTemplatePickerCard";
import "../../../templatePicker/emailBuilderTemplatePicker.scss";
import { EmailBuilderApplyTemplateConfirmModal } from "./EmailBuilderApplyTemplateConfirmModal";
import { EmailBuilderSkeleton } from "../../EmailBuilderSkeleton";
import "./emailBuilderTemplatesPanel.scss";
import { StatusCode } from "api/protocols";

type TTemplatesPanelSessionCache = {
  system: IEmailMarketingTemplateListItem[];
  facility: IEmailMarketingTemplateListItem[];
  hasMore: boolean;
};

let templatesPanelSessionCache: TTemplatesPanelSessionCache | null = null;

type TPendingApply = {
  templateId: number;
  templateName: string;
};

const SKELETON_KEYS = [
  "email-builder-templates-skeleton-0",
  "email-builder-templates-skeleton-1",
  "email-builder-templates-skeleton-2",
];

function renderTemplatesPanelSkeletonCard(key: string) {
  return (
    <div key={key} className="email-builder-templates-panel__skeleton-card" aria-hidden="true">
      <div className="email-builder-templates-panel__skeleton-preview" />
      <EmailBuilderSkeleton
        text
        width={12}
        className="email-builder-templates-panel__skeleton-card-title"
      />
    </div>
  );
}

function renderTemplatesPanelLoadingSections(): IBuilderToolbarMenuViewSection[] {
  return [
    {
      title: <EmailBuilderSkeleton text width={10} />,
      content: (
        <div
          className="builder-toolbar-menu-view-card-stack email-builder-templates-panel__card-stack"
          aria-busy="true"
        >
          {SKELETON_KEYS.map(renderTemplatesPanelSkeletonCard)}
        </div>
      ),
    },
  ];
}

interface IEmailBuilderTemplatesToolbarMenuProps {
  presentation?: TBuilderToolbarMenuViewPresentation;
  onDismissPanel?: () => void;
  onClose?: () => void;
  closeAriaLabel?: string;
}

export function EmailBuilderTemplatesToolbarMenu(props: IEmailBuilderTemplatesToolbarMenuProps) {
  const presentation = props.presentation ?? "desktop-panel";
  const model = useEmailBuilderTemplatesPanel({
    presentation,
    onDismissPanel: props.onDismissPanel,
  });

  return (
    <>
      <BuilderToolbarMenuView
        title={model.t("Templates")}
        presentation={presentation}
        onClose={props.onClose}
        closeAriaLabel={props.closeAriaLabel}
        sections={model.buildToolbarSections(true)}
        sectionsContainerRef={model.scrollRootRef}
      />
      {model.confirmModal}
    </>
  );
}

function resolveTemplatesInfiniteScrollRoot(
  sectionsEl: HTMLDivElement | null,
  presentation: TBuilderToolbarMenuViewPresentation,
): Element | null {
  if (sectionsEl === null) {
    return null;
  }

  if (presentation === "mobile-sheet-body") {
    const sheetBody = sectionsEl.closest(".ui-bottom-sheet-dialog-body");
    if (sheetBody instanceof HTMLElement) {
      return sheetBody;
    }
  }

  return sectionsEl;
}

function useEmailBuilderTemplatesPanel(options: {
  presentation: TBuilderToolbarMenuViewPresentation;
  onDismissPanel?: () => void;
}) {
  const { presentation, onDismissPanel } = options;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activePanel = useAppSelector(store => store.emailBuilderStore.activePanel);
  const workspace = useAppSelector(store => store.emailBuilderStore.workspace);
  const excludeTemplateId =
    workspace != null && workspace.kind === "template" ? workspace.id : null;

  const scrollRootRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const applyingRef = useRef(false);

  const [system, setSystem] = useState<IEmailMarketingTemplateListItem[]>(
    () => templatesPanelSessionCache?.system ?? [],
  );
  const [facility, setFacility] = useState<IEmailMarketingTemplateListItem[]>(
    () => templatesPanelSessionCache?.facility ?? [],
  );
  const [hasMore, setHasMore] = useState(
    () => templatesPanelSessionCache?.hasMore ?? false,
  );
  const [hasLoadedOnce, setHasLoadedOnce] = useState(
    () => templatesPanelSessionCache != null,
  );
  const [loading, setLoading] = useState(() => templatesPanelSessionCache == null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingApply, setPendingApply] = useState<TPendingApply | null>(null);
  const [confirming, setConfirming] = useState(false);

  const visibleSystem = filterEmailBuilderTemplatesPanelList(system, excludeTemplateId);
  const visibleFacility = filterEmailBuilderTemplatesPanelList(facility, excludeTemplateId);

  const loadInitial = useCallback(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setErrorMessage(null);

    return GetEmailMarketingTemplates(
      {
        limit: EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT,
        offset: 0,
      },
      false,
    )
      .then(templatesRes => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        if (templatesRes.status !== StatusCode.OK || templatesRes.data == null) {
          setErrorMessage(
            typeof templatesRes.message === "string" && templatesRes.message.length > 0
              ? templatesRes.message
              : t("Could not load email templates."),
          );
          return;
        }

        const nextSystem = templatesRes.data.system;
        const nextFacility = templatesRes.data.facility;
        const nextHasMore = emailBuilderTemplatesHasMore(
          nextFacility.length,
          EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT,
        );

        templatesPanelSessionCache = {
          system: nextSystem,
          facility: nextFacility,
          hasMore: nextHasMore,
        };

        setSystem(nextSystem);
        setFacility(nextFacility);
        setHasMore(nextHasMore);
        setHasLoadedOnce(true);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setErrorMessage(t("Could not load email templates."));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [t]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMoreRef.current || errorMessage != null) {
      return Promise.resolve();
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const offset = facility.length;

    return GetEmailMarketingTemplates(
      {
        limit: EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT,
        offset,
      },
      false,
    )
      .then(templatesRes => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        if (templatesRes.status !== StatusCode.OK || templatesRes.data == null) {
          dispatch(
            showError(
              typeof templatesRes.message === "string" && templatesRes.message.length > 0
                ? templatesRes.message
                : t("Could not load more templates."),
            ),
          );
          return;
        }

        const page = templatesRes.data.facility;
        const nextFacility = [...facility, ...page];
        const nextHasMore = emailBuilderTemplatesHasMore(
          page.length,
          EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT,
        );

        templatesPanelSessionCache = {
          system: templatesRes.data.system.length > 0 ? templatesRes.data.system : system,
          facility: nextFacility,
          hasMore: nextHasMore,
        };

        setFacility(nextFacility);
        setHasMore(nextHasMore);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        dispatch(showError(t("Could not load more templates.")));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      });
  }, [dispatch, errorMessage, facility, hasMore, loading, system, t]);

  useEffect(() => {
    if (hasLoadedOnce) {
      return;
    }

    void loadInitial();

    return () => {
      requestIdRef.current += 1;
    };
  }, [hasLoadedOnce, loadInitial]);

  useEffect(() => {
    const sectionsEl = scrollRootRef.current;
    const scrollRoot = resolveTemplatesInfiniteScrollRoot(sectionsEl, presentation);
    const sentinel = loadMoreSentinelRef.current;

    if (!scrollRoot || !sentinel || !hasMore || loading || loadingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const hit = entries.some(entry => entry.isIntersecting);
        if (!hit) {
          return;
        }

        void loadMore();
      },
      { root: scrollRoot, rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [presentation, hasMore, loading, loadingMore, facility.length, errorMessage, activePanel, loadMore]);

  function handleSelectTemplate(templateId: number) {
    const template =
      visibleSystem.find(item => item.id === templateId) ??
      visibleFacility.find(item => item.id === templateId);

    if (template == null) {
      return;
    }

    setPendingApply({ templateId: template.id, templateName: template.name });
  }

  async function handleConfirmApply() {
    if (pendingApply == null || applyingRef.current) {
      return;
    }

    applyingRef.current = true;
    setConfirming(true);

    const templateRes = await GetEmailMarketingTemplate(pendingApply.templateId, false);

    if (templateRes.status !== StatusCode.OK || templateRes.data == null) {
      applyingRef.current = false;
      setConfirming(false);
      dispatch(
        showError(
          typeof templateRes.message === "string" && templateRes.message.length > 0
            ? templateRes.message
            : t("Could not load email template."),
        ),
      );
      return;
    }

    const validation = normalizeEmailBuilderTemplateActiveDocument(
      templateRes.data.active_document,
    );

    if (!validation.ok) {
      applyingRef.current = false;
      setConfirming(false);
      dispatch(
        showError(
          validation.errors[0] ?? t("This template could not be applied."),
        ),
      );
      return;
    }

    dispatch(emailBuilderCloseInlineContent());
    dispatch(emailBuilderCloseBlockSettings());
    runEditorThunk(editorThunks.loadDocument(validation.document, "blank"));
    dispatch(emailBuilderSyncEditorDocument(validation.document));

    onDismissPanel?.();

    applyingRef.current = false;
    setConfirming(false);
    setPendingApply(null);
  }

  const isEmpty =
    hasLoadedOnce &&
    !loading &&
    errorMessage == null &&
    visibleSystem.length === 0 &&
    visibleFacility.length === 0;

  function renderErrorState() {
    return (
      <div className="email-builder-templates-panel__error">
        <p className="email-builder-templates-panel__error-text">{errorMessage}</p>
        <ButtonNew type="secondary" size="medium" onClick={() => void loadInitial()}>
          {t("Retry")}
        </ButtonNew>
      </div>
    );
  }

  function renderEmptyState() {
    return (
      <MessageDisplay
        centered
        icon="table-layout"
        title={t("No templates available")}
        content={t("Templates will appear here when they are available.")}
      />
    );
  }

  function renderTemplateCards(templates: IEmailMarketingTemplateListItem[]) {
    return (
      <div className="builder-toolbar-menu-view-card-stack email-builder-templates-panel__card-stack">
        {templates.map(template => (
          <EmailBuilderTemplatePickerCard
            key={template.id}
            template={template}
            disabled={confirming}
            onSelect={handleSelectTemplate}
          />
        ))}
      </div>
    );
  }

  function buildToolbarSections(includeLoadMore: boolean): IBuilderToolbarMenuViewSection[] {
    if (loading) {
      return renderTemplatesPanelLoadingSections();
    }

    if (errorMessage != null) {
      return [{ content: renderErrorState() }];
    }

    if (isEmpty) {
      return [{ content: renderEmptyState() }];
    }

    const sections: IBuilderToolbarMenuViewSection[] = [];

    if (visibleSystem.length > 0) {
      sections.push({
        title: t("System"),
        content: renderTemplateCards(visibleSystem),
      });
    }

    if (visibleFacility.length > 0) {
      sections.push({
        title: t("My templates"),
        content: (
          <>
            {renderTemplateCards(visibleFacility)}
            {includeLoadMore && loadingMore ? (
              <div className="email-builder-templates-panel__load-more-skeleton" aria-busy="true">
                {renderTemplatesPanelSkeletonCard("email-builder-templates-load-more-skeleton")}
              </div>
            ) : null}
            {includeLoadMore && hasMore ? (
              <div
                ref={loadMoreSentinelRef}
                className="email-builder-templates-panel__sentinel"
                aria-hidden="true"
              />
            ) : null}
          </>
        ),
      });
    }

    return sections;
  }

  return {
    t,
    scrollRootRef,
    buildToolbarSections,
    confirmModal: (
      <EmailBuilderApplyTemplateConfirmModal
        open={pendingApply != null}
        confirming={confirming}
        templateName={pendingApply?.templateName ?? null}
        onClose={() => {
          if (!confirming) {
            setPendingApply(null);
          }
        }}
        onConfirm={() => {
          void handleConfirmApply();
        }}
      />
    ),
  };
}
